import { useEffect, useMemo } from 'react';

import { WatchQueryFetchPolicy } from 'apollo-client';

import { useLoyaltyUserRewardsQuery } from 'generated/graphql-gateway';
import { useLoyaltyRewardsQuery } from 'generated/sanity-graphql';
import { useLoyaltyContext } from 'state/loyalty';
import { LoyaltyEngineReward, LoyaltyReward } from 'state/loyalty/types';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';

interface IParsedEngineRewards {
  engineRewardIds: string[];
  engineRewardsMap: {
    [rewardId: string]: LoyaltyEngineReward;
  };
}
export type ISanityRewardsMap = Record<LoyaltyReward['_id'], LoyaltyReward>;

export const useLoyaltyRewardsList = (fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network') => {
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();

  const { loyaltyEnabled, loyaltyUser, createAvailableLoyaltyRewardsMap } = useLoyaltyContext();
  // To Do: move this logic to loyalty GQL server in order to make 1 call on the frontend
  // and to avoid creating rewards map
  const {
    data: engineResponse,
    loading: engineRewardsLoading,
    refetch: refetchRewards,
  } = useLoyaltyUserRewardsQuery({
    skip: !loyaltyEnabled || !loyaltyUser?.id,
    variables: {
      loyaltyId: loyaltyUser?.id || '',
      where: {
        ignorePointBalance: true,
        serviceMode: serviceMode || undefined,
        storeId: store?.number,
      },
    },
    fetchPolicy,
  });

  const { engineRewardIds, engineRewardsMap } = useMemo(
    () =>
      (engineResponse?.loyaltyUser?.rewards || []).reduce(
        (acc: IParsedEngineRewards, reward) => {
          // Extract engineIds to query for rewards in sanity
          if (reward?.id) {
            acc.engineRewardIds.push(reward.id);
            acc.engineRewardsMap[reward.id] = reward;
          }
          return acc;
        },
        { engineRewardIds: [], engineRewardsMap: {} }
      ),
    [engineResponse]
  );

  const { data, loading: sanityRewardsLoading } = useLoyaltyRewardsQuery({
    skip: !engineRewardIds?.length,
  });

  const rewardsByEngineId = useMemo(
    () => data?.allRewards.filter(reward => engineRewardsMap[reward?.loyaltyEngineId || '']),
    [data, engineRewardsMap]
  );

  const sanityRewardsMap = useMemo(() => {
    if (!rewardsByEngineId) {
      return null;
    }
    return rewardsByEngineId.reduce((acc: ISanityRewardsMap, reward) => {
      acc[reward._id] = reward;
      return acc;
    }, {});
  }, [rewardsByEngineId]);

  useEffect(() => {
    if (engineResponse?.loyaltyUser?.rewards?.length) {
      createAvailableLoyaltyRewardsMap(engineResponse?.loyaltyUser.rewards);
    }
  }, [createAvailableLoyaltyRewardsMap, engineResponse]);

  const rewardsLoading = engineRewardsLoading || sanityRewardsLoading;

  return {
    rewards: rewardsByEngineId || null,
    loading: !data?.allRewards && !engineResponse && rewardsLoading,
    engineRewardsMap,
    sanityRewardsMap,
    refetchRewards,
  };
};
